import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';
import { DiamondIcon } from './icons/DiamondIcon';
import { useStripeCheckout } from '../hooks/useStripeCheckout';
import { useState } from 'react';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PaywallModal = ({ isOpen, onClose }: PaywallModalProps) => {
  const { user } = useUser();
  const { redirectToCheckout } = useStripeCheckout();
  const [isLoading, setIsLoading] = useState(false);

  const features = [
    {
      icon: "🔍",
      title: "Deep Transaction Insights",
      description: "Unlock advanced spending patterns"
    },
    {
      icon: "💰",
      title: "Money Wrapped Page",
      description: "Get your personalized yearly summary"
    },
    {
      icon: "📊",
      title: "Advanced Analytics",
      description: "View detailed financial breakdowns"
    },
    {
      icon: "✨",
      title: "Premium Insights",
      description: "Access all insight panels"
    }
  ];

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      await redirectToCheckout();
    } catch (error) {
      console.error('Failed to redirect to checkout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 border-2 border-black shadow-brutal"
          >
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <DiamondIcon className="w-16 h-16 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Unlock Premium Insights</h2>
              <p className="text-gray-600">Get the full picture of your finances</p>
            </div>

            {/* Features List */}
            <div className="space-y-4 mb-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 p-3 rounded-xl border border-gray-200">
                  <span className="text-2xl">{feature.icon}</span>
                  <div>
                    <h3 className="font-semibold">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing */}
            <div className="text-center mb-6">
              <div className="text-3xl font-bold mb-2">$9.99<span className="text-lg text-gray-600">/month</span></div>
              <p className="text-sm text-gray-600">Cancel anytime • Instant access</p>
            </div>

            {/* Button */}
            <button
              onClick={handleSubscribe}
              disabled={isLoading}
              className="w-full bg-blue-500 text-white py-3 rounded-xl font-medium
                       border-2 border-black shadow-brutal
                       hover:bg-blue-600 transition-colors
                       active:translate-x-[2px] active:translate-y-[2px] active:shadow-none
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Unlock Premium Now'}
            </button>

            {/* Terms */}
            <div className="mt-4 text-center text-sm text-gray-500">
              By upgrading, you agree to our Terms and Privacy Policy
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaywallModal;
